import { useState } from 'react';
import { DayPicker, type Modifiers } from 'react-day-picker';

import { cn } from '@/ux-common/lib/utils';

import { CALENDAR_CLASS_NAMES, CALENDAR_RANGE_MODIFIERS_CLASS_NAMES } from './calendar.constants';
import { CALENDAR_MODE, type CalendarProps } from './calendar.types';
import { getInitialMonth } from './calendar.utils';
import { CaptionWithNav } from './calendar-header';

/**
 * Calendar — registry wrapper around react-day-picker's DayPicker.
 *
 * Visual styling is delivered through CSS classes:
 * - `.calendar` (auto-applied) is the root contract; themes paint it and
 *   carry the default (md) sizing.
 * - `.calendar-mode-{single|range|multiple}` is auto-applied for mode-aware
 *   selection styling.
 * - `.calendar-sm` / `.calendar-md` / `.calendar-lg` are opt-in size classes
 *   the consumer composes via `className`. Bare `.calendar` renders at md.
 * - Slot classes (`.calendar-day`, `.calendar-day-button`, `.calendar-weekday`,
 *   `.calendar-month-caption`, etc.) are mapped onto the DayPicker's
 *   `classNames` prop.
 *
 * @example
 * ```tsx
 * <Calendar mode="single" selected={date} onSelect={setDate} />
 * <Calendar mode="range" className="calendar-sm" selected={range} onSelect={setRange} />
 * ```
 */
export const Calendar = (props: CalendarProps) => {
  const {
    className,
    classNames,
    showOutsideDays = true,
    timeZone,
    mode,
    defaultMonth,
    onDayClick,
    onMonthChange,
    localeCode = 'en-US',
    ...restProps
  } = props;

  const [month, setMonth] = useState<Date>(() =>
    getInitialMonth(mode, defaultMonth, 'selected' in props ? props.selected : undefined, timeZone)
  );

  const handleDayClick = (day: Date, activeModifiers: Modifiers, e: React.MouseEvent) => {
    if (day && day.getMonth() !== month.getMonth()) setMonth(day);
    onDayClick?.(day, activeModifiers, e);
  };

  const handleMonthChange = (newMonth: Date) => {
    setMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  const baseProps = {
    'data-component': 'Calendar',
    className: cn('calendar', `calendar-mode-${mode}`, className),
    classNames: { ...CALENDAR_CLASS_NAMES, ...classNames },
    components: {
      CaptionLabel: () => <CaptionWithNav locale={localeCode} />,
    },
    hideNavigation: true as const,
    month,
    onDayClick: handleDayClick,
    onMonthChange: handleMonthChange,
    showOutsideDays,
    timeZone,
    ...restProps,
  };

  switch (mode) {
    case CALENDAR_MODE.SINGLE:
      return (
        <DayPicker
          {...baseProps}
          mode={CALENDAR_MODE.SINGLE}
          selected={'selected' in props ? props.selected : undefined}
          onSelect={'onSelect' in props ? props.onSelect : undefined}
        />
      );
    case CALENDAR_MODE.RANGE:
      return (
        <DayPicker
          {...baseProps}
          mode={CALENDAR_MODE.RANGE}
          modifiersClassNames={CALENDAR_RANGE_MODIFIERS_CLASS_NAMES}
          selected={'selected' in props ? props.selected : undefined}
          onSelect={'onSelect' in props ? props.onSelect : undefined}
        />
      );
    case CALENDAR_MODE.MULTIPLE:
      return (
        <DayPicker
          {...baseProps}
          mode={CALENDAR_MODE.MULTIPLE}
          selected={'selected' in props ? props.selected : undefined}
          onSelect={'onSelect' in props ? props.onSelect : undefined}
        />
      );
  }
};

Calendar.displayName = 'Calendar';
